import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Paper, Stepper, Step, StepLabel } from '@mui/material';
import authService from '../../services/authService';
import SocialLoginButtons from './SocialLoginButtons';

const SignupForm = ({ onSwitchToLogin, onForgotPassword }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    email: '',
    verificationCode: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: ''
  });
  const steps = ['Enter Details', 'Verify Email', 'Complete'];

  const handleSendCode = async () => {
    try {
      await authService.signUp(
        formData.email,
        formData.password,
        formData.firstName,
        formData.lastName
      );
      setCurrentStep(1);
    } catch (error) {
      alert('Signup error: ' + error.message);
    }
  };

  const handleVerifyCode = async () => {
    try {
      await authService.confirmSignUp(formData.email, formData.verificationCode);
      setCurrentStep(2);
    } catch (error) {
      alert('Verification failed: ' + error.message);
    }
  };

  const handleCompleteSignup = () => {
    onSwitchToLogin();
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Paper elevation={8} sx={{ p: 4, width: '100%', maxWidth: 400, borderRadius: 2 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>Create Account</Typography>
          <Stepper activeStep={currentStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {currentStep === 0 && (
          <>
            <SocialLoginButtons 
              onLoading={(loading) => {}} 
              onError={(error) => alert(error)} 
            />
            <Typography variant="body2" sx={{ mb: 2, textAlign: 'center' }}>
              Enter your details to create account
            </Typography>
            <TextField
              fullWidth
              label="First Name"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Last Name"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              sx={{ mb: 2 }}
              helperText="At least 8 characters"
            />
            <TextField
              fullWidth
              label="Confirm Password"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              sx={{ mb: 3 }}
              error={formData.confirmPassword && formData.password !== formData.confirmPassword}
              helperText={formData.confirmPassword && formData.password !== formData.confirmPassword ? 'Passwords do not match' : ''}
            />
            <Button
              onClick={handleSendCode}
              variant="contained"
              fullWidth
              size="large"
              disabled={!formData.email || !formData.firstName || !formData.lastName || !formData.password || !formData.confirmPassword || formData.password !== formData.confirmPassword}
            >
              Send Verification Code
            </Button>
          </>
        )}

        {currentStep === 1 && (
          <>
            <Typography variant="body2" sx={{ mb: 2, textAlign: 'center' }}>
              We've sent a verification code to:<br/>
              <strong>{formData.email}</strong>
            </Typography>
            <TextField
              fullWidth
              label="Verification Code"
              placeholder="Enter 6-digit code"
              value={formData.verificationCode}
              onChange={(e) => handleInputChange('verificationCode', e.target.value)}
              sx={{ mb: 3 }}
              inputProps={{ maxLength: 6 }}
            />
            <Button
              onClick={handleVerifyCode}
              variant="contained"
              fullWidth
              size="large"
              disabled={!formData.verificationCode}
            >
              Verify Code
            </Button>
          </>
        )}

        {currentStep === 2 && (
          <>
            <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', color: 'success.main' }}>
              ✓ Account Created!
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, textAlign: 'center' }}>
              Your account has been created successfully. You can now sign in with your email and password.
            </Typography>
            <Button
              onClick={handleCompleteSignup}
              variant="contained"
              fullWidth
              size="large"
            >
              Continue to Sign In
            </Button>
          </>
        )}

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Already have an account?{' '}
            <Button variant="text" onClick={onSwitchToLogin}>
              Sign In
            </Button>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Forgot your password?{' '}
            <Button variant="text" onClick={onForgotPassword}>
              Reset Password
            </Button>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default SignupForm;